/**
 * useWebSocketInvalidation — bridges the global ws_manager broadcasts
 * to React Query so list/detail queries refetch on real backend
 * changes instead of via wall-clock `refetchInterval` polling.
 *
 * Backend channels (dashboard/ws_manager.py — broadcast_event):
 *   build, chat, task, project, application, agent, marketing,
 *   preview, log
 *
 * Mapping rule of thumb: invalidate the *closest matching* query keys
 * for each channel. We don't try to be surgical (no per-record diff)
 * — invalidating a few extra queries is cheaper than missing a
 * legitimate update, and React Query already coalesces overlapping
 * invalidations.
 *
 * What this REPLACES on a typical signed-in build-page render:
 *   - useApprovals  (was: 5–15s timer)
 *   - useVerification (was: 5s timer)
 *   - useDeployment (was: 5s timer when on the Deploy tab)
 *   - useChat / useTaskGraph / useTaskPlan / useProjectFiles
 *     (had no timer but refetched on WS-driven `invalidateQueries`
 *     called from BuildPage — that call site is no longer needed)
 *
 * What we DON'T touch:
 *   - Cross-cutting admin pages that legitimately need timed polling
 *     (useMonitoring, useStatus, useUsageAnalytics, etc.)
 *   - Hooks where the underlying state has no WS event yet
 *     (useWalletBalance — see TODO at the bottom of this file).
 */
import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useWsStore } from '@/stores/wsStore'

/**
 * A frame on the shared /ws socket. Room events are built by
 * dashboard/ws_manager.py::broadcast_event, and nothing else sends them:
 *
 *   {"type": "event", "channel": "chat", "event": {...body, "project_id": "P"}, "ts": "..."}
 *
 * so `project_id` / `application_id` live inside `event`, never at the top
 * level. The only other frames are the heartbeat `{"type": "ping"}` and a join
 * refusal `{"type": "error"}` (same file). The build worker's chat reply arrives
 * in the same envelope, relayed by dashboard/event_bus_bridge.py.
 */
interface WsFrame {
  type?: string
  channel?: string
  event?: {
    type?: string
    project_id?: string | null
    application_id?: string | null
    build_id?: string | null
  } | null
}

/**
 * Map a /ws frame to the React Query keys it makes stale. Returns an empty
 * array for frames that are not room events.
 *
 * Keys are prefixes of the keys the consumer hooks register. Where a hook adds
 * the application as a third element (useChat, useTaskGraph and useProjectFiles
 * use `applicationId ?? ''`; useVerification and useTaskPlan use it as is), the
 * prefix stops at the project, because many frames carry no application_id and
 * a project-wide refresh is cheap.
 *
 * Pulled out as a pure function so it can be unit-tested without a
 * live WebSocket.
 */
export function queryKeysForFrame(frame: WsFrame): unknown[][] {
  const keys: unknown[][] = []
  if (frame.type !== 'event' || !frame.channel || !frame.event) return keys
  const channel = frame.channel
  const projectId = frame.event.project_id
  const applicationId = frame.event.application_id
  if (!projectId) return keys

  // Build channel: build lifecycle (build_started from the chat route,
  // deployment status). Affects verification, task graph/plan, files, history.
  if (channel === 'build') {
    keys.push(['verification', projectId])
    keys.push(['task-graph', projectId])
    keys.push(['taskPlan', projectId])
    keys.push(['project-files', projectId])
    if (applicationId) {
      keys.push(['build-history', applicationId])
    }
    return keys
  }

  // Chat channel: assistant turns (including the build worker's reply),
  // routing decisions, system messages. useChat: ['chat', project, app ?? ''].
  if (channel === 'chat') {
    keys.push(['chat', projectId])
    return keys
  }

  // Task channel: task_graph updates and task lifecycle.
  if (channel === 'task') {
    keys.push(['task-graph', projectId])
    keys.push(['taskPlan', projectId])
    return keys
  }

  // Project channel: create/update/delete of projects + files.
  // useProjects: ['projects', workspaceId].
  if (channel === 'project') {
    keys.push(['projects'])
    keys.push(['project-files', projectId])
    return keys
  }

  // Application channel: create/update/delete of applications.
  // useApplications: ['applications', p]; useDefaultApplication adds 'default'.
  if (channel === 'application') {
    keys.push(['projects'])
    keys.push(['applications', projectId])
    return keys
  }

  // Agent channel: agent_start / agent_complete during a build.
  if (channel === 'agent') {
    keys.push(['task-graph', projectId])
    return keys
  }

  // Preview channel: preview started/updated. useAppPreviews:
  // ['app-previews', project, app].
  if (channel === 'preview') {
    keys.push(['app-previews', projectId])
    return keys
  }

  // Marketing + log channels don't have a tight query-key match today.
  // Add as needed.
  return keys
}

/**
 * Subscribe to the global WebSocket from anywhere in the React tree.
 * Mount this once at the AppLayout level — siblings can keep using
 * `useQuery` as usual; invalidation happens transparently.
 */
export function useWebSocketInvalidation(): void {
  const socket = useWsStore((s) => s.socket)
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!socket) return
    const handler = (ev: MessageEvent) => {
      let frame: WsFrame
      try {
        frame = JSON.parse(ev.data)
      } catch {
        return // Non-JSON frames (raw text) are ignored.
      }
      if (!frame || typeof frame !== 'object') return
      const keys = queryKeysForFrame(frame)
      for (const key of keys) {
        queryClient.invalidateQueries({ queryKey: key })
      }
    }
    socket.addEventListener('message', handler)
    return () => {
      socket.removeEventListener('message', handler)
    }
  }, [socket, queryClient])
}

// TODO: wire a `wallet` WS channel on the backend so useWalletBalance
// can drop its eventual refetchInterval. Currently the balance only
// refetches on mount + manual `invalidateQueries(['wallet'])` calls.
